import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useGetSyncLogs } from '~/hooks/apis/experiences/useGetSyncLogs';

import { SyncLog } from '~/services/ExperiencesService';

function LogDetails({ log }: { log: SyncLog['log'] }) {
  const { reason, score, aiReason } = log as { reason?: string; score?: number; aiReason?: string };
  const hasKnownFields = reason !== undefined || score !== undefined || aiReason !== undefined;

  if (!hasKnownFields)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );

  return (
    <Stack spacing={0.5}>
      {score !== undefined && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
            Score
          </Typography>
          <Chip label={score} size="small" color={score >= 0.7 ? 'success' : score >= 0.4 ? 'warning' : 'error'} />
        </Stack>
      )}
      {reason !== undefined && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, pt: '2px' }}>
            Reason
          </Typography>
          <Typography variant="body2">{reason}</Typography>
        </Stack>
      )}
      {aiReason !== undefined && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, pt: '2px' }}>
            AI Reason
          </Typography>
          <Typography variant="body2">{aiReason}</Typography>
        </Stack>
      )}
    </Stack>
  );
}

export default function SyncLogs() {
  const [offerIdInput, setOfferIdInput] = useState('');
  const [searchedOfferId, setSearchedOfferId] = useState('');

  const { data: logs = [], isFetching, isError, error } = useGetSyncLogs(searchedOfferId);

  const handleSearch = () => {
    const trimmed = offerIdInput.trim();
    if (!trimmed) return;
    setSearchedOfferId(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={4}>
        <TextField
          label="Offer ID"
          value={offerIdInput}
          onChange={(e) => setOfferIdInput(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          sx={{ width: 340 }}
        />
        <Button variant="contained" onClick={handleSearch} disabled={!offerIdInput.trim() || isFetching}>
          Search
        </Button>
      </Stack>

      {isFetching && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error)?.message ?? 'Failed to fetch sync logs.'}
        </Alert>
      )}

      {!isFetching && !isError && searchedOfferId && logs.length === 0 && (
        <Typography color="text.secondary">No logs found for offer ID: {searchedOfferId}</Typography>
      )}

      {!isFetching && logs.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Offer ID</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    {log.offerId}
                  </TableCell>
                  <TableCell>{log.provider}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(log.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <LogDetails log={log.log} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
